import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../services/axiosInstance';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import SubjectIcon from '@mui/icons-material/Subject';
import NumbersIcon from '@mui/icons-material/Numbers';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ListIcon from '@mui/icons-material/List';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FullPageLayoutProfile, ICategory, ITemplateField } from '../types/damTypes';
import { v4 as uuidv4 } from 'uuid';
import {
  getFullPageLayoutGroupLabel,
  getFullPageLayoutGroups,
  normalizeFullPageLayoutGroup,
  normalizeFullPageLayoutPriority,
} from '../modules/fullPageAssetDetail/layoutProfiles';

// Define FieldType and IFieldOption based on ITemplateField
type FieldType = ITemplateField['type'];
type IFieldOption = NonNullable<ITemplateField['options']>[number];

interface TemplateFieldEditorProps {
  fields: ITemplateField[];
  onChange: (fields: ITemplateField[]) => void;
  onSave?: () => void;
  loading?: boolean;
  isProductsTemplate?: boolean;
  isWineTemplate?: boolean;
  fullPageLayoutProfile?: FullPageLayoutProfile;
}

/** Strip UTF-8 BOM (common for Excel CSV). */
function stripUtf8Bom(text: string): string {
  return text.replace(/^\uFEFF/, '');
}

/**
 * First CSV field on a line; supports quoted fields and "" escapes.
 * For single-column exports the whole line is typically one field.
 */
function parseFirstCsvField(line: string): string {
  const s = line.trim();
  if (!s) return '';
  if (s[0] === '"') {
    let out = '';
    for (let i = 1; i < s.length; i++) {
      const c = s[i];
      if (c === '"') {
        if (s[i + 1] === '"') {
          out += '"';
          i++;
          continue;
        }
        return out.trim();
      }
      out += c;
    }
    return out.trim();
  }
  return s;
}

function normalizeHeaderToken(value: string): string {
  return value.trim().replace(/^"|"$/g, '').toLowerCase();
}

function isLikelyOptionHeaderRow(parsedValue: string): boolean {
  const n = normalizeHeaderToken(parsedValue);
  return n === 'term name' || n === 'name' || n === 'namn';
}

function parseOptionsFromCsvText(text: string): { values: string[]; skippedHeader: boolean } {
  const raw = stripUtf8Bom(text);
  const lines = raw.split(/\r?\n/);
  const values: string[] = [];
  let skippedHeader = false;
  let isFirstNonEmpty = true;

  for (const line of lines) {
    const cell = parseFirstCsvField(line);
    if (!cell) continue;
    if (isFirstNonEmpty && isLikelyOptionHeaderRow(cell)) {
      skippedHeader = true;
      isFirstNonEmpty = false;
      continue;
    }
    isFirstNonEmpty = false;
    values.push(cell);
  }

  return { values, skippedHeader };
}

function parseCommaSeparatedValues(text: string): string[] {
  return Array.from(
    new Set(
      String(text || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
}

// Get icon for field type
const getFieldTypeIcon = (type: FieldType) => {
  switch (type) {
    case 'text':
      return <TextFieldsIcon />;
    case 'textarea':
      return <SubjectIcon />;
    case 'number':
      return <NumbersIcon />;
    case 'date':
      return <CalendarMonthIcon />;
    case 'checkbox':
      return <CheckBoxIcon />;
    case 'select':
      return <ListIcon />;
    case 'list':
      return <ListIcon />;
    case 'bulletList':
      return <ListIcon />;
    case 'tags':
      return <LocalOfferIcon />;
    case 'url':
      return <LinkIcon />;
    case 'reference':
      return <LinkIcon />;
    case 'separator':
      return <DragIndicatorIcon />;
    default:
      return <TextFieldsIcon />;
  }
};

// Get display name for field type
const getFieldTypeName = (type: FieldType) => {
  switch (type) {
    case 'text':
      return 'Text';
    case 'textarea':
      return 'Text Area';
    case 'number':
      return 'Number';
    case 'date':
      return 'Date';
    case 'checkbox':
      return 'Checkbox';
    case 'select':
      return 'Dropdown';
    case 'list':
      return 'List';
    case 'bulletList':
      return 'Punktlista';
    case 'tags':
      return 'Tags';
    case 'url':
      return 'URL';
    case 'reference':
      return 'Searchable Reference';
    case 'separator':
      return 'Separator';
    default:
      return type;
  }
};

// External integration systems available
const EXTERNAL_SYSTEMS = [
  { 
    id: 'validoo', 
    name: 'Validoo', 
    description: 'Enhanced Validoo API with organized, user-friendly field names', 
    logo: '', 
    fields: []
  },
  { 
    id: 'dabas', 
    name: 'Dabas', 
    description: 'Dabas produktdatabas med svensk produktinformation', 
    logo: '', 
    fields: []
  }
];

// Sortable Field Item Component
interface SortableFieldItemProps {
  field: ITemplateField;
  groupFields: ITemplateField[];
  onEdit: (fieldId: string) => void;
  onDelete: (fieldId: string) => void;
  onMoveField: (fieldId: string, direction: 'up' | 'down') => void;
  showDuringUpload: boolean;
  showDuringEdit: boolean;
  fullPageLayoutProfile: FullPageLayoutProfile;
}

const SortableFieldItem: React.FC<SortableFieldItemProps> = ({
  field,
  groupFields,
  onEdit,
  onDelete,
  onMoveField,
  showDuringUpload,
  showDuringEdit,
  fullPageLayoutProfile
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{ 
        p: field.type === 'separator' ? 1 : 2, 
        mb: 1, 
        display: 'flex',
        alignItems: 'center',
        cursor: isDragging ? 'grabbing' : 'grab',
        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
        border: isDragging ? '2px dashed #1976d2' : '1px solid transparent',
        bgcolor: field.type === 'separator' ? '#f5f5f5' : 'white',
        borderLeft: field.type === 'separator' ? '4px solid #1976d2' : 'none'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {/* Drag Handle */}
        <Box 
          {...attributes} 
          {...listeners}
          sx={{ 
            mr: 1, 
            color: 'text.secondary', 
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <DragIndicatorIcon />
        </Box>

        <Box sx={{ mr: 1, color: field.type === 'separator' ? 'primary.main' : 'text.secondary' }}>
          {getFieldTypeIcon(field.type)}
        </Box>
        
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: field.type === 'separator' ? 'bold' : 'normal',
            color: field.type === 'separator' ? 'primary.main' : 'inherit'
          }}>
            {field.label}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip 
              label={getFieldTypeName(field.type)} 
              size="small" 
              variant="outlined"
              color={field.type === 'separator' ? 'primary' : 'default'}
            />
            {field.type === 'separator' && field.fullPageLayoutGroup && (
              <Chip
                label={`Stor vy: ${getFullPageLayoutGroupLabel(fullPageLayoutProfile, field.fullPageLayoutGroup)}`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
            {field.type === 'separator' && field.fullPageLayoutPriority !== undefined && (
              <Chip
                label={`Prio ${field.fullPageLayoutPriority}`}
                size="small"
                variant="outlined"
              />
            )}
            {field.type !== 'separator' && (
              <>
                {field.isExternal && (
                  <Chip label="External" size="small" color="info" icon={<CloudSyncIcon />} />
                )}
                {field.showDuringUpload && (
                  <Chip label="Upload" size="small" color={showDuringUpload ? "primary" : "default"} />
                )}
                {field.showDuringEdit && (
                  <Chip label="Edit" size="small" color={showDuringEdit ? "primary" : "default"} />
                )}
                {field.showInChainDropdown && (
                  <Chip label="Kedjedropdown" size="small" color="secondary" />
                )}
                {field.showInWineDropdown && (
                  <Chip label="VIN-dropdown" size="small" color="secondary" variant="outlined" />
                )}
              </>
            )}
          </Box>
          {field.description && field.type !== 'separator' && (
            <Typography variant="caption" color="text.secondary">
              {field.description}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => onEdit(field.id)} color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(field.id)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

const TemplateFieldEditor: React.FC<TemplateFieldEditorProps> = ({
  fields,
  onChange,
  onSave,
  loading,
  isProductsTemplate = false,
  isWineTemplate = false,
  fullPageLayoutProfile = 'other',
}) => {
  const [isAddFieldOpen, setIsAddFieldOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Form state variables
  const [fieldName, setFieldName] = useState<string>('');
  const [fieldLabel, setFieldLabel] = useState<string>('');
  const [fieldType, setFieldType] = useState<FieldType>('text');
  const [fieldDescription, setFieldDescription] = useState<string>('');
  const [fieldPlaceholder, setFieldPlaceholder] = useState<string>('');
  const [fieldDefaultValue, setFieldDefaultValue] = useState<any>('');
  const [fieldRequired, setFieldRequired] = useState<boolean>(false);
  const [showDuringUpload, setShowDuringUpload] = useState<boolean>(true);
  const [showDuringEdit, setShowDuringEdit] = useState<boolean>(true);
  const [showInProductHeader, setShowInProductHeader] = useState<boolean>(false);
  const [showInChainDropdown, setShowInChainDropdown] = useState<boolean>(false);
  const [showInWineDropdown, setShowInWineDropdown] = useState<boolean>(false);
  const [excludeFromSearch, setExcludeFromSearch] = useState<boolean>(false);
  const [prioritizeInSearch, setPrioritizeInSearch] = useState<boolean>(false);
  const [fieldOptions, setFieldOptions] = useState<IFieldOption[]>([]);
  const [fieldOptionsImportFeedback, setFieldOptionsImportFeedback] = useState<{
    message: string;
    severity: 'success' | 'warning';
  } | null>(null);
  const fieldOptionsFileInputRef = useRef<HTMLInputElement>(null);
  const [newOptionValue, setNewOptionValue] = useState<string>('');
  const [currentOrder, setCurrentOrder] = useState<number>(0);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [hideLabelInDisplay, setHideLabelInDisplay] = useState<boolean>(false);
  const [mainCategories, setMainCategories] = useState<ICategory[]>([]);
  const [loadingMainCategories, setLoadingMainCategories] = useState<boolean>(false);
  const [referenceCategoryId, setReferenceCategoryId] = useState<string>('');
  const [referenceSearchFieldsText, setReferenceSearchFieldsText] = useState<string>('title');
  const [referenceDisplayField, setReferenceDisplayField] = useState<string>('title');
  const [referenceSecondaryDisplayField, setReferenceSecondaryDisplayField] = useState<string>('');
  const [fullPageLayoutGroup, setFullPageLayoutGroup] = useState<string>('');
  const [fullPageLayoutPriority, setFullPageLayoutPriority] = useState<string>('');
  
  // External integration state
  const [isExternal, setIsExternal] = useState<boolean>(false);
  const [externalSource, setExternalSource] = useState<string>('');
  
  // Enhanced Validoo fields state
  const [validooFields, setValidooFields] = useState<any[]>([]);
  const [validooFieldSearch, setValidooFieldSearch] = useState<string>('');
  const [loadingValidooFields, setLoadingValidooFields] = useState<boolean>(false);
  
  // Dabas fields state
  const [dabasFields, setDabasFields] = useState<any[]>([]);
  const [dabasFieldSearch, setDabasFieldSearch] = useState<string>('');
  const [loadingDabasFields, setLoadingDabasFields] = useState<boolean>(false);
  const [dabasGtin, setDabasGtin] = useState<string>('07316150970340'); // Default test GTIN
  
  // External field path state (for the selected mapping)
  const [externalFieldPath, setExternalFieldPath] = useState<string>('');

  useEffect(() => {
    let active = true;

    const loadMainCategories = async () => {
      setLoadingMainCategories(true);
      try {
        const response = await axiosInstance.get<ICategory[]>('/api/categories/main');
        if (!active) return;
        setMainCategories(Array.isArray(response.data) ? response.data : []);
      } catch (loadError) {
        console.error('Failed to load main categories for reference field config:', loadError);
        if (!active) return;
        setMainCategories([]);
      } finally {
        if (active) {
          setLoadingMainCategories(false);
        }
      }
    };

    void loadMainCategories();

    return () => {
      active = false;
    };
  }, []);
  
  // Fetch enhanced Validoo fields when user selects Validoo as source
  useEffect(() => {
    if (externalSource === 'validoo') {
      setLoadingValidooFields(true);
      console.log('🔍 Fetching enhanced Validoo fields...');
      fetch('/api/validoo/fields/comprehensive')
        .then(response => response.text())
        .then(responseText => {
          console.log('📄 Raw response (first 300 chars):', responseText.substring(0, 300));
          
          try {
            const data = JSON.parse(responseText);
            console.log('✅ Enhanced fields loaded. Count:', Array.isArray(data) ? data.length : 'unknown');
            
            if (Array.isArray(data)) {
              setValidooFields(data);
            } else if (data.success && data.fields) {
              setValidooFields(data.fields);
            } else {
              console.warn('⚠️ Unexpected data format:', data);
              setValidooFields([]);
            }
          } catch (jsonError) {
            console.error('❌ Failed to parse JSON:', jsonError);
            setValidooFields([]);
          }
        })
        .catch(error => {
          console.error('❌ Fetch error:', error);
          setValidooFields([]);
        })
        .finally(() => {
          setLoadingValidooFields(false);
        });
    }
  }, [externalSource]);
  
  // Fetch Dabas fields when user selects Dabas as source and provides GTIN
  const fetchDabasFields = async (gtin: string) => {
    if (!gtin.trim()) {
      setDabasFields([]);
      return;
    }
    
    setLoadingDabasFields(true);
    console.log('🔍 Fetching Dabas fields for GTIN:', gtin);
    
    try {
      const response = await axiosInstance.get(`/api/dabas/fields?gtin=${encodeURIComponent(gtin)}`);
      const data = response.data;
      
      if (data.success && data.fields) {
        console.log('✅ Dabas fields loaded. Count:', data.fields.length);
        setDabasFields(data.fields);
      } else {
        console.warn('⚠️ Dabas fields fetch failed:', data.message);
        setDabasFields([]);
      }
    } catch (error: any) {
      console.error('❌ Dabas fetch error:', error.response?.data?.message || error.message);
      setDabasFields([]);
    } finally {
      setLoadingDabasFields(false);
    }
  };
  
  // Fetch Dabas fields when source changes to dabas
  useEffect(() => {
    if (externalSource === 'dabas' && dabasGtin) {
      fetchDabasFields(dabasGtin);
    }
  }, [externalSource]);
  
  // Filter Dabas fields by search
  const filteredDabasFields = dabasFields.filter(field => {
    if (!dabasFieldSearch) return true;
    const search = dabasFieldSearch.toLowerCase();
    return (
      field.path?.toLowerCase().includes(search) ||
      field.label?.toLowerCase().includes(search) ||
      field.example?.toLowerCase().includes(search) ||
      field.category?.toLowerCase().includes(search)
    );
  });
  
  // Group and filter Validoo fields by category
  const groupedValidooFields = React.useMemo(() => {
    // First filter by search
    const filtered = validooFields.filter(field => {
      const matchesSearch = !validooFieldSearch || 
        field.label.toLowerCase().includes(validooFieldSearch.toLowerCase()) ||
        field.path.toLowerCase().includes(validooFieldSearch.toLowerCase()) ||
        field.example.toLowerCase().includes(validooFieldSearch.toLowerCase()) ||
        field.category.toLowerCase().includes(validooFieldSearch.toLowerCase());
      
      return matchesSearch;
    });

    // Then group by category
    const grouped = filtered.reduce((acc, field) => {
      const category = field.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(field);
      return acc;
    }, {} as Record<string, typeof validooFields>);

    // Sort categories for consistent display
    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      // Prioritize key categories
      const priority = [
        '📁 Grundläggande Information',
        '📁 Marknadsföring & Beskrivning', 
        '📁 Mått & Dimensioner (Med Enhet)',
        '📁 Mått & Dimensioner',
        '📁 Näringsvärden',
        '📁 Allergener & Innehåll',
        '📁 Förvaring & Hållbarhet',
        '📁 Avrunnen Vikt',
        '📁 Systeminformation'
      ];
      
      const aIndex = priority.indexOf(a);
      const bIndex = priority.indexOf(b);
      
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });

    return sortedCategories.map(category => ({
      category,
      fields: grouped[category]
    }));
  }, [validooFields, validooFieldSearch]);

  // Flatten for backward compatibility
  const filteredValidooFields = groupedValidooFields.flatMap(group => group.fields);
  
  // Reset form fields
  const resetForm = () => {
    setSelectedFieldId(null);
    setFieldName('');
    setFieldLabel('');
    setFieldType('text');
    setFieldDescription('');
    setFieldPlaceholder('');
    setFieldDefaultValue('');
    setFieldRequired(false);
    setShowDuringUpload(true);
    setShowDuringEdit(true);
    setShowInProductHeader(false);
    setShowInChainDropdown(false);
    setShowInWineDropdown(false);
    setExcludeFromSearch(false);
    setPrioritizeInSearch(false);
    setFieldOptions([]);
    setFieldOptionsImportFeedback(null);
    setNewOptionValue('');
    setIsExternal(false);
    setExternalSource('');
    setExternalFieldPath('');
    setError(null);
    setIsReadOnly(false);
    setHideLabelInDisplay(false);
    setValidooFieldSearch('');
    setDabasFieldSearch('');
    setReferenceCategoryId('');
    setReferenceSearchFieldsText('title');
    setReferenceDisplayField('title');
    setReferenceSecondaryDisplayField('');
    setFullPageLayoutGroup('');
    setFullPageLayoutPriority('');
  };
  
  // Handle opening dialog to add a new field
  const handleAddField = () => {
    resetForm();
    setIsAddFieldOpen(true);
  };
  
  // Handle editing an existing field
  const handleEditField = (fieldId: string) => {
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;
    
    setSelectedFieldId(fieldId);
    setFieldName(field.name || '');
    setFieldLabel(field.label);
    setFieldType(field.type);
    setFieldDescription(field.description || '');
    setFieldPlaceholder(field.placeholder || '');
    setFieldDefaultValue(field.defaultValue !== undefined ? field.defaultValue : '');
    setFieldRequired(field.validation?.required || false);
    setShowDuringUpload(field.showDuringUpload === undefined ? true : field.showDuringUpload);
    setShowDuringEdit(field.showDuringEdit === undefined ? true : field.showDuringEdit);
    setShowInProductHeader(field.showInProductHeader === undefined ? false : field.showInProductHeader);
    setShowInChainDropdown(field.showInChainDropdown === true);
    setShowInWineDropdown(field.showInWineDropdown === true);
    setExcludeFromSearch(field.excludeFromSearch === true);
    setPrioritizeInSearch(field.prioritizeInSearch === true);
    setFieldOptions(field.options || []);
    setFieldOptionsImportFeedback(null);
    setIsExternal(field.isExternal === undefined ? false : field.isExternal);
    setExternalSource(field.externalSource || '');
    setExternalFieldPath((field as any).externalFieldPath || '');
    setCurrentOrder(field.order === undefined ? 0 : field.order);
    setIsAddFieldOpen(true);
    setIsReadOnly(field.isReadOnly === undefined ? false : field.isReadOnly);
    setHideLabelInDisplay(field.hideLabelInDisplay === true);
    setReferenceCategoryId((field as any).referenceConfig?.categoryId || '');
    setReferenceSearchFieldsText(
      Array.isArray((field as any).referenceConfig?.searchFields) && (field as any).referenceConfig.searchFields.length > 0
        ? (field as any).referenceConfig.searchFields.join(', ')
        : 'title',
    );
    setReferenceDisplayField((field as any).referenceConfig?.displayField || 'title');
    setReferenceSecondaryDisplayField((field as any).referenceConfig?.secondaryDisplayField || '');
    setFullPageLayoutGroup(
      field.type === 'separator' && field.fullPageLayoutGroup
        ? normalizeFullPageLayoutGroup(fullPageLayoutProfile, field.fullPageLayoutGroup)
        : ''
    );
    setFullPageLayoutPriority(
      field.type === 'separator' && field.fullPageLayoutPriority !== undefined
        ? String(field.fullPageLayoutPriority)
        : ''
    );
  };
  
  // Handle deleting a field
  const handleDeleteField = (fieldId: string) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      onChange(fields.filter(field => field.id !== fieldId));
    }
  };
  
  // Handle adding an option for select/list fields
  const handleAddOption = () => {
    const trimmedValue = newOptionValue.trim();
    if (!trimmedValue) {
      return;
    }
    
    if (fieldOptions.some(option => option.value === trimmedValue)) {
      setError('This value already exists');
      return;
    }
    
    // Use same value for both value and label
    setFieldOptions([...fieldOptions, { value: trimmedValue, label: trimmedValue }]);
    setNewOptionValue('');
    setError(null);
  };
  
  // Handle sorting options alphabetically
  const handleSortOptions = () => {
    const sorted = [...fieldOptions].sort((a, b) => 
      a.label.localeCompare(b.label, 'sv', { sensitivity: 'base' })
    );
    setFieldOptions(sorted);
  };
  
  // Handle removing an option
  const handleRemoveOption = (value: string) => {
    setFieldOptions(fieldOptions.filter(option => option.value !== value));
    if (fieldDefaultValue === value) {
      setFieldDefaultValue('');
    }
  };

  const handleFieldOptionsFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      const text = await file.text();
      const { values, skippedHeader } = parseOptionsFromCsvText(text);
      if (values.length === 0) {
        setFieldOptionsImportFeedback(null);
        setError('Inga rader hittades i filen (eller endast rubrikrad).');
        return;
      }

      setError(null);
      const seen = new Set(fieldOptions.map(option => option.value));
      const newOptions: IFieldOption[] = [];
      let added = 0;
      let skippedDup = 0;

      for (const value of values) {
        if (!value) continue;
        if (seen.has(value)) {
          skippedDup++;
          continue;
        }
        seen.add(value);
        newOptions.push({ value, label: value });
        added++;
      }

      setFieldOptions([...fieldOptions, ...newOptions]);

      const parts: string[] = [];
      if (added > 0) {
        parts.push(`${added} alternativ tillagda`);
      } else {
        parts.push('Inga nya alternativ (alla fanns redan)');
      }
      if (skippedDup > 0) {
        parts.push(`${skippedDup} hoppades över som dubbletter`);
      }
      if (skippedHeader) {
        parts.push('rubrikrad ignorerad');
      }
      setFieldOptionsImportFeedback({
        message: parts.join(' · '),
        severity: added > 0 ? 'success' : 'warning',
      });
    } catch (err) {
      setFieldOptionsImportFeedback(null);
      setError(err instanceof Error ? err.message : 'Kunde inte läsa filen');
    }
  };
  
  // Handle saving a field (add or update)
  const handleSaveField = () => {
    if (!fieldName.trim()) {
      setError('Field name is required');
      return;
    }
    
    if (!fieldLabel.trim()) {
      setError('Field label is required');
      return;
    }
    
    if (!/^[a-zA-Z0-9_]+$/.test(fieldName)) {
      setError('Field name can only contain letters, numbers, and underscores');
      return;
    }
    
    // Skip certain validations for separators
    if (fieldType !== 'separator') {
      if ((fieldType === 'select' || fieldType === 'list') && fieldOptions.length === 0) {
        setError('Dropdown and list fields require at least one option');
        return;
      }

      if (fieldType === 'select' && fieldDefaultValue && !fieldOptions.some(option => option.value === fieldDefaultValue)) {
        setError('Dropdown default must match one of the field options');
        return;
      }
      
      if (isExternal && !externalSource) {
        setError('Please select an external data source');
        return;
      }
    }

    const normalizedFullPagePriority = normalizeFullPageLayoutPriority(fullPageLayoutPriority);
    if (fieldType === 'separator' && fullPageLayoutPriority.trim() && normalizedFullPagePriority === undefined) {
      setError('Prioritet på stora sidan måste vara ett heltal som är 0 eller högre');
      return;
    }
    
    const validation = fieldRequired && fieldType !== 'separator' ? { required: true } : undefined;
    
    const maxOrder = fields.reduce((max, field) => Math.max(max, field.order === undefined ? -1 : field.order), 0);
    
    const newFieldData: ITemplateField = {
      id: selectedFieldId || `field_${fieldName}_${uuidv4().slice(0, 8)}`,
      name: fieldName,
      label: fieldLabel,
      type: fieldType,
      description: fieldType === 'separator' ? undefined : (fieldDescription.trim() || undefined),
      placeholder: fieldType === 'separator' ? undefined : (fieldPlaceholder.trim() || undefined),
      defaultValue: fieldType === 'separator' ? undefined : (fieldType === 'reference' ? [] : fieldDefaultValue),
      validation: validation,
      showDuringUpload: showDuringUpload,
      showDuringEdit: fieldType === 'separator' ? true : showDuringEdit,
      showInProductHeader: fieldType === 'separator' ? false : showInProductHeader,
      showInChainDropdown: fieldType === 'separator' ? false : showInChainDropdown,
      showInWineDropdown: fieldType === 'separator' ? false : showInWineDropdown,
      excludeFromSearch: fieldType === 'separator' ? false : excludeFromSearch,
      prioritizeInSearch: fieldType === 'separator' ? false : prioritizeInSearch,
      options: fieldType === 'select' || fieldType === 'list' ? fieldOptions : undefined,
      order: selectedFieldId 
        ? (fields.find(f => f.id === selectedFieldId)?.order || 0)
        : (maxOrder + 1),
      group: undefined,
      isExternal: fieldType === 'separator' || fieldType === 'reference' ? false : isExternal,
      externalSource: fieldType === 'separator' || fieldType === 'reference' ? undefined : (isExternal ? externalSource.trim() || undefined : undefined),
      externalFieldPath: fieldType === 'separator' || fieldType === 'reference' ? undefined : (isExternal ? externalFieldPath.trim() || undefined : undefined),
      isReadOnly: fieldType === 'separator' ? false : isReadOnly,
      hideLabelInDisplay: fieldType === 'separator' ? false : hideLabelInDisplay,
      fullPageLayoutGroup: fieldType === 'separator' && fullPageLayoutGroup
        ? normalizeFullPageLayoutGroup(fullPageLayoutProfile, fullPageLayoutGroup)
        : undefined,
      fullPageLayoutPriority: fieldType === 'separator' ? normalizedFullPagePriority : undefined,
      referenceConfig: fieldType === 'reference'
        ? {
            entityType: 'asset',
            ...(referenceCategoryId ? { categoryId: referenceCategoryId } : {}),
            searchFields: parseCommaSeparatedValues(referenceSearchFieldsText || 'title'),
            displayField: referenceDisplayField.trim() || 'title',
            ...(referenceSecondaryDisplayField.trim()
              ? { secondaryDisplayField: referenceSecondaryDisplayField.trim() }
              : {}),
          }
        : undefined,
    };

    let updatedFieldsArray = [...fields];
    if (selectedFieldId) {
      updatedFieldsArray = updatedFieldsArray.map(field => {
        if (field.id === selectedFieldId) {
          return newFieldData;
        }
        return field;
      });
    } else {
      updatedFieldsArray = [...updatedFieldsArray, newFieldData];
    }
    
    onChange(updatedFieldsArray);
    setIsAddFieldOpen(false);
    resetForm();
  };
  
  // Handle drag end event for reordering fields
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const sortedFields = fields.sort((a, b) => {
      const orderA = a.order === undefined ? Infinity : a.order;
      const orderB = b.order === undefined ? Infinity : b.order;
      if (orderA === orderB) {
        return (a.label || '').localeCompare(b.label || '');
      }
      return orderA - orderB;
    });

    const oldIndex = sortedFields.findIndex(f => f.id === active.id);
    const newIndex = sortedFields.findIndex(f => f.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedFields = arrayMove(sortedFields, oldIndex, newIndex);
    
    reorderedFields.forEach((field, index) => {
      field.order = index;
    });

    onChange(reorderedFields);
  };

  // Move a field up or down within the list
  const handleMoveField = (fieldId: string, direction: 'up' | 'down') => {
    const sortedFields = fields.sort((a, b) => {
      const orderA = a.order === undefined ? Infinity : a.order;
      const orderB = b.order === undefined ? Infinity : b.order;
      if (orderA === orderB) {
        return (a.label || '').localeCompare(b.label || '');
      }
      return orderA - orderB;
    });

    const currentIndex = sortedFields.findIndex(f => f.id === fieldId);
    let newIndex = currentIndex;

    if (direction === 'up' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < sortedFields.length - 1) {
      newIndex = currentIndex + 1;
    }

    if (newIndex !== currentIndex) {
      const reorderedFields = arrayMove(sortedFields, currentIndex, newIndex);
      reorderedFields.forEach((field, index) => {
        field.order = index;
      });

      onChange(reorderedFields);
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Template Fields</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onSave && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={onSave}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Update Template'}
            </Button>
          )}
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleAddField}
          >
            Add Custom Field
          </Button>
        </Box>
      </Box>
      
      <DndContext 
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={fields.map(field => field.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields
            .sort((a, b) => {
              const orderA = a.order === undefined ? Infinity : a.order;
              const orderB = b.order === undefined ? Infinity : b.order;
              if (orderA === orderB) {
                return a.label.localeCompare(b.label);
              }
              return orderA - orderB;
            })
            .map((field) => (
              <SortableFieldItem
                key={field.id}
                field={field}
                groupFields={fields}
                onEdit={handleEditField}
                onDelete={handleDeleteField}
                onMoveField={handleMoveField}
                showDuringUpload={field.showDuringUpload}
                showDuringEdit={field.showDuringEdit}
                fullPageLayoutProfile={fullPageLayoutProfile}
              />
            ))
          }
        </SortableContext>
      </DndContext>
      
      {fields.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No custom fields defined for this template. Click "Add Custom Field" to create one.
        </Alert>
      )}
      
      {/* Field Editor Dialog */}
      <Dialog open={isAddFieldOpen} onClose={() => setIsAddFieldOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedFieldId ? 'Edit Field' : 'Add New Field'}
        </DialogTitle>
        
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="div" sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
            <Box sx={{ gridColumn: 'span 6' }}>
              <TextField
                label="Field Name (System ID)"
                required
                fullWidth
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                helperText="Use only letters, numbers, and underscores. This is used as a unique identifier in the system."
                margin="normal"
              />
            </Box>
            
            <Box sx={{ gridColumn: 'span 6' }}>
              <TextField
                label="Field Label"
                required
                fullWidth
                value={fieldLabel}
                onChange={(e) => setFieldLabel(e.target.value)}
                helperText="Display name shown to users"
                margin="normal"
              />
            </Box>
            
            <Box sx={{ gridColumn: 'span 6' }}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="field-type-label">Field Type</InputLabel>
                <Select
                  labelId="field-type-label"
                  value={fieldType}
                  onChange={(e) => {
                    const nextType = e.target.value as FieldType;
                    setFieldType(nextType);
                    if (nextType === 'separator') {
                      // Keep separators hidden by default on upload
                      setShowDuringUpload(false);
                      setShowDuringEdit(true);
                    } else if (nextType === 'reference') {
                      setIsExternal(false);
                      setExternalSource('');
                      setExternalFieldPath('');
                    }
                  }}
                  label="Field Type"
                >
                  <MenuItem value="text">Text Field</MenuItem>
                  <MenuItem value="textarea">Text Area</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="checkbox">Checkbox</MenuItem>
                  <MenuItem value="select">Dropdown List</MenuItem>
                  <MenuItem value="list">List</MenuItem>
                  <MenuItem value="bulletList">Punktlista</MenuItem>
                  <MenuItem value="tags">Tags</MenuItem>
                  <MenuItem value="url">URL</MenuItem>
                  <MenuItem value="reference">Searchable Reference</MenuItem>
                  <MenuItem value="separator">Section Separator</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {fieldType !== 'separator' && (
              <>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    label="Description"
                    fullWidth
                    value={fieldDescription}
                    onChange={(e) => setFieldDescription(e.target.value)}
                    helperText="Help text displayed below the field"
                    margin="normal"
                  />
                </Box>
                
                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    label="Placeholder"
                    fullWidth
                    value={fieldPlaceholder}
                    onChange={(e) => setFieldPlaceholder(e.target.value)}
                    helperText="Placeholder text shown when the field is empty"
                    margin="normal"
                  />
                </Box>
              </>
            )}
            
            {fieldType === 'reference' && (
              <Box sx={{ gridColumn: 'span 12' }}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', mt: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Reference Search Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This field searches DAM assets and stores both an internal reference and a label snapshot for export.
                  </Typography>

                  <FormControl fullWidth margin="normal" disabled={loadingMainCategories}>
                    <InputLabel id="reference-category-scope-label">Category Scope</InputLabel>
                    <Select
                      labelId="reference-category-scope-label"
                      value={referenceCategoryId}
                      onChange={(e) => setReferenceCategoryId(e.target.value)}
                      label="Category Scope"
                    >
                      <MenuItem value="">Entire DAM</MenuItem>
                      {mainCategories.map((category) => (
                        <MenuItem key={category._id} value={category._id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Search Fields"
                    fullWidth
                    margin="normal"
                    value={referenceSearchFieldsText}
                    onChange={(e) => setReferenceSearchFieldsText(e.target.value)}
                    helperText="Comma-separated. Example: title, artikelnummer"
                  />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <TextField
                        label="Primary Label Field"
                        fullWidth
                        margin="normal"
                        value={referenceDisplayField}
                        onChange={(e) => setReferenceDisplayField(e.target.value)}
                        helperText="Shown in chips and saved as the label snapshot. Example: title"
                      />
                    </Box>
                    <Box>
                      <TextField
                        label="Secondary Label Field"
                        fullWidth
                        margin="normal"
                        value={referenceSecondaryDisplayField}
                        onChange={(e) => setReferenceSecondaryDisplayField(e.target.value)}
                        helperText="Optional subtitle in search results. Example: artikelnummer"
                      />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}

            {/* Enhanced External Data Integration */}
            {fieldType !== 'separator' && fieldType !== 'reference' && (
              <Box sx={{ gridColumn: 'span 12' }}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1">External Data Integration</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isExternal}
                          onChange={(e) => setIsExternal(e.target.checked)}
                        />
                      }
                      label="Enable External Data"
                    />
                  </Box>
                  
                  {isExternal && (
                    <Box mt={2}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="external-source-label">Data Source</InputLabel>
                        <Select
                          labelId="external-source-label"
                          value={externalSource}
                          onChange={(e) => setExternalSource(e.target.value)}
                          label="Data Source"
                        >
                          <MenuItem value="">Select a source</MenuItem>
                          {EXTERNAL_SYSTEMS.map(system => (
                            <MenuItem key={system.id} value={system.id}>{system.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {externalSource === 'validoo' && (
                        <>
                          <Box mt={2}>
                            <Typography variant="subtitle2" gutterBottom>
                              Enhanced Validoo Fields
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Select from organized, user-friendly field names. The system now returns 
                              categorized fields with readable labels instead of technical XML paths.
                            </Typography>
                            
                            {/* Search functionality */}
                            <TextField
                              label="Search fields"
                              size="small"
                              fullWidth
                              value={validooFieldSearch}
                              onChange={(e) => setValidooFieldSearch(e.target.value)}
                              placeholder="Search by name, path, or example..."
                              sx={{ mb: 2 }}
                            />
                            
                            <FormControl fullWidth margin="normal">
                              <InputLabel id="validoo-field-label">Validoo Field (source)</InputLabel>
                              <Select
                                labelId="validoo-field-label"
                                value={externalFieldPath || ''}
                                onChange={(e) => {
                                  const value = e.target.value as string;
                                  console.log('🔍 Select onChange triggered with value:', value);
                                  
                                  if (!value) {
                                    setExternalFieldPath('');
                                    return;
                                  }
                                  
                                  const selectedField = validooFields.find(f => f.path === value);
                                  console.log('📄 Found field in validooFields:', selectedField ? 'YES' : 'NO');
                                  
                                  if (selectedField) {
                                    console.log('✅ Updating external field path:', selectedField.path);
                                    setExternalFieldPath(selectedField.path);
                                    // Only suggest label if empty
                                    if (!fieldLabel) {
                                      setFieldLabel(selectedField.label);
                                    }
                                  } else {
                                    console.error('❌ Field not found in validooFields, available paths:', validooFields.map(f => f.path));
                                  }
                                }}
                                label="Validoo Field (source)"
                                disabled={loadingValidooFields}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 400,
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  {loadingValidooFields ? 'Loading enhanced fields...' : 'Select a Validoo field'}
                                </MenuItem>
                                
                                {/* Show warning for legacy fields */}
                                {externalFieldPath && !validooFields.find(f => f.path === externalFieldPath) && (
                                  <MenuItem value={externalFieldPath} sx={{ bgcolor: 'warning.light', fontStyle: 'italic' }}>
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        ⚠️ Legacy Field
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {externalFieldPath.length > 50 ? `${externalFieldPath.substring(0, 50)}...` : externalFieldPath}
                                      </Typography>
                                      <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>
                                        Consider selecting a new organized field below
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                )}
                                
                                {filteredValidooFields.map((field: any) => (
                                  <MenuItem 
                                    key={field.path} 
                                    value={field.path}
                                  >
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        [{field.category}] {field.label}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Path: {field.path.length > 45 ? `${field.path.substring(0, 45)}...` : field.path}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                        Example: {field.example ? (field.example.length > 55 ? `${field.example.substring(0, 55)}...` : field.example) : 'No example'}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Showing {filteredValidooFields.length} of {validooFields.length} enhanced fields 
                              {loadingValidooFields ? ' (Loading...)' : ` across ${groupedValidooFields.length} categories`}
                            </Typography>
                          </Box>
                          
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              Validoo integration requires assets to have a valid GTIN (Global Trade Item Number). 
                              Make sure your template includes a GTIN field marked as external.
                            </Typography>
                          </Alert>
                        </>
                      )}
                      
                      {externalSource === 'dabas' && (
                        <>
                          <Box mt={2}>
                            <Typography variant="subtitle2" gutterBottom>
                              Dabas Produktfält
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Ange ett GTIN för att hämta tillgängliga fält från Dabas.
                            </Typography>
                            
                            {/* GTIN input and fetch button */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                              <TextField
                                label="GTIN (för att hämta fält)"
                                size="small"
                                value={dabasGtin}
                                onChange={(e) => setDabasGtin(e.target.value)}
                                placeholder="07316150970340"
                                sx={{ flexGrow: 1 }}
                              />
                              <Button 
                                variant="outlined" 
                                size="small"
                                onClick={() => fetchDabasFields(dabasGtin)}
                                disabled={loadingDabasFields || !dabasGtin.trim()}
                              >
                                {loadingDabasFields ? 'Hämtar...' : 'Hämta fält'}
                              </Button>
                            </Box>
                            
                            {/* Search functionality */}
                            {dabasFields.length > 0 && (
                              <TextField
                                label="Sök fält"
                                size="small"
                                fullWidth
                                value={dabasFieldSearch}
                                onChange={(e) => setDabasFieldSearch(e.target.value)}
                                placeholder="Sök på namn, kategori eller exempel..."
                                sx={{ mb: 2 }}
                              />
                            )}
                            
                            <FormControl fullWidth margin="normal">
                              <InputLabel id="dabas-field-label">Dabas-fält (källa)</InputLabel>
                              <Select
                                labelId="dabas-field-label"
                                value={externalFieldPath || ''}
                                onChange={(e) => {
                                  const value = e.target.value as string;
                                  console.log('🔍 Dabas field selected:', value);
                                  
                                  if (!value) {
                                    setExternalFieldPath('');
                                    return;
                                  }
                                  
                                  const selectedField = dabasFields.find(f => f.path === value);
                                  if (selectedField) {
                                    setExternalFieldPath(selectedField.path);
                                    // Only suggest label if empty
                                    if (!fieldLabel) {
                                      setFieldLabel(selectedField.label);
                                    }
                                  }
                                }}
                                label="Dabas-fält (källa)"
                                disabled={loadingDabasFields || dabasFields.length === 0}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 400,
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  {loadingDabasFields ? 'Hämtar fält...' : 
                                   dabasFields.length === 0 ? 'Ange GTIN och hämta fält först' : 
                                   'Välj ett Dabas-fält'}
                                </MenuItem>
                                
                                {filteredDabasFields.map((field: any) => (
                                  <MenuItem 
                                    key={field.path} 
                                    value={field.path}
                                  >
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        [{field.category}] {field.label}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                        Exempel: {field.example ? (field.example.length > 60 ? `${field.example.substring(0, 60)}...` : field.example) : 'Inget exempel'}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            
                            {dabasFields.length > 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Visar {filteredDabasFields.length} av {dabasFields.length} fält
                              </Typography>
                            )}
                          </Box>
                          
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              Dabas-integration kräver att assets har ett giltigt GTIN-fält. 
                              Se till att din template har ett GTIN-fält markerat som externt.
                            </Typography>
                          </Alert>
                        </>
                      )}
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        External fields can be populated from integrated systems like Validoo, Dabas, or other custom APIs.
                        These fields will be updated automatically based on the integration settings.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            )}
            
            {(fieldType === 'select' || fieldType === 'list') && (
              <>
                <Box sx={{ gridColumn: 'span 12', mt: 1 }}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="h6">
                      Field Options
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <input
                        type="file"
                        ref={fieldOptionsFileInputRef}
                        accept=".csv,text/csv,text/plain"
                        style={{ display: 'none' }}
                        onChange={handleFieldOptionsFileChange}
                      />
                      <Button
                        size="small"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fieldOptionsFileInputRef.current?.click()}
                      >
                        Importera CSV
                      </Button>
                      {fieldOptions.length > 1 && (
                        <Button
                          size="small"
                          startIcon={<SortByAlphaIcon />}
                          onClick={handleSortOptions}
                        >
                          Sortera A-Ö
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    label="Lägg till alternativ"
                    fullWidth
                    value={newOptionValue}
                    onChange={(e) => setNewOptionValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddOption();
                      }
                    }}
                    placeholder="Skriv ett värde och tryck Enter"
                    helperText="Tryck Enter för att lägga till ett alternativ, eller importera en enkel CSV med en kolumn (t.ex. Term Name som rubrik)."
                    size="small"
                  />
                </Box>

                {fieldOptionsImportFeedback && (
                  <Box sx={{ gridColumn: 'span 12' }}>
                    <Alert
                      severity={fieldOptionsImportFeedback.severity}
                      onClose={() => setFieldOptionsImportFeedback(null)}
                    >
                      {fieldOptionsImportFeedback.message}
                    </Alert>
                  </Box>
                )}

                <Box sx={{ gridColumn: 'span 12', mt: 1 }}>
                  {fieldOptions.length === 0 ? (
                    <Alert severity="info">
                      Inga alternativ tillagda. Skriv ett värde ovan och tryck Enter, eller använd Importera CSV.
                    </Alert>
                  ) : (
                    <Box>
                      {fieldType === 'select' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Klicka på ett alternativ för att göra det till default.
                          </Typography>
                          {fieldDefaultValue && (
                            <Button size="small" onClick={() => setFieldDefaultValue('')}>
                              Rensa default
                            </Button>
                          )}
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {fieldOptions.map(option => {
                          const isDefaultOption = fieldType === 'select' && fieldDefaultValue === option.value;
                          return (
                            <Chip
                              key={option.value}
                              label={isDefaultOption ? `${option.label} (Default)` : option.label}
                              color={isDefaultOption ? 'primary' : 'default'}
                              variant={isDefaultOption ? 'filled' : 'outlined'}
                              icon={isDefaultOption ? <CheckCircleIcon /> : undefined}
                              onClick={fieldType === 'select' ? () => setFieldDefaultValue(option.value) : undefined}
                              onDelete={() => handleRemoveOption(option.value)}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </>
            )}
            
            {fieldType !== 'separator' && (
              <>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Field Settings
                  </Typography>
                </Box>
                
                <Box sx={{ gridColumn: 'span 3' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fieldRequired}
                        onChange={(e) => setFieldRequired(e.target.checked)}
                      />
                    }
                    label="Required Field"
                  />
                </Box>
                
                <Box sx={{ gridColumn: 'span 3' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showDuringUpload}
                        onChange={(e) => setShowDuringUpload(e.target.checked)}
                      />
                    }
                    label="Show During Upload"
                  />
                </Box>
                
                <Box sx={{ gridColumn: 'span 3' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showDuringEdit}
                        onChange={(e) => setShowDuringEdit(e.target.checked)}
                      />
                    }
                    label="Show During Edit"
                  />
                </Box>
                
                <Box sx={{ gridColumn: 'span 3' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showInProductHeader}
                        onChange={(e) => setShowInProductHeader(e.target.checked)}
                      />
                    }
                    label="Visa i produktheader"
                  />
                </Box>

                {isProductsTemplate && (
                  <Box sx={{ gridColumn: 'span 3' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showInChainDropdown}
                          onChange={(e) => setShowInChainDropdown(e.target.checked)}
                        />
                      }
                      label="Visas i kedjenummer-dropdown"
                    />
                  </Box>
                )}

                {isWineTemplate && (
                  <Box sx={{ gridColumn: 'span 3' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showInWineDropdown}
                          onChange={(e) => setShowInWineDropdown(e.target.checked)}
                        />
                      }
                      label="Visas i VIN-dropdown"
                    />
                  </Box>
                )}
                
                <Box sx={{ gridColumn: 'span 3' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isReadOnly}
                        onChange={(e) => setIsReadOnly(e.target.checked)}
                      />
                    }
                    label="Read Only"
                  />
                </Box>

                <Box sx={{ gridColumn: 'span 3' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideLabelInDisplay}
                        onChange={(e) => setHideLabelInDisplay(e.target.checked)}
                      />
                    }
                    label="Dölj fältnamn i visningsläge"
                  />
                </Box>

                <Box sx={{ gridColumn: 'span 3' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={excludeFromSearch}
                        onChange={(e) => setExcludeFromSearch(e.target.checked)}
                      />
                    }
                    label="Exkludera från sök"
                  />
                </Box>

                <Box sx={{ gridColumn: 'span 3' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={prioritizeInSearch}
                        onChange={(e) => setPrioritizeInSearch(e.target.checked)}
                      />
                    }
                    label="Prioritera i sök"
                  />
                </Box>
              </>
            )}
            
            {fieldType === 'separator' && (
              <Box sx={{ gridColumn: 'span 12', mt: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="full-page-layout-group-label">Layoutgrupp på stora sidan</InputLabel>
                    <Select
                      labelId="full-page-layout-group-label"
                      value={fullPageLayoutGroup}
                      label="Layoutgrupp på stora sidan"
                      onChange={(e) => setFullPageLayoutGroup(e.target.value)}
                    >
                      <MenuItem value="">Standard</MenuItem>
                      {getFullPageLayoutGroups(fullPageLayoutProfile).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Prioritet på stora sidan"
                    type="number"
                    value={fullPageLayoutPriority}
                    onChange={(e) => setFullPageLayoutPriority(e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                    helperText="Lägre nummer visas högre upp. Tomt värde följer mallens ordning."
                    fullWidth
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={showDuringUpload}
                      onChange={(e) => setShowDuringUpload(e.target.checked)}
                    />
                  }
                  label="Show During Upload"
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Separator fields</strong> are used to create visual divisions between sections of fields.
                    They don't store data and only need a name and label.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setIsAddFieldOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveField} variant="contained" color="primary">
            {selectedFieldId ? 'Update Field' : 'Add Field'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateFieldEditor; 
